import { useCallback, useEffect, useRef } from "react";

import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import IconButton from "@mui/material/IconButton";
import { grey } from "@mui/material/colors";
import useTheme from "@mui/material/styles/useTheme";
import useMediaQuery from "@mui/material/useMediaQuery";

import { misc, primary } from "app/themes/colors";
import { usePatientNotesDrawer } from "features/patients/patient-notes-drawer";
import useGetTopBarHeight from "hooks/use-get-topbar-height.hook";

import PatientDrawerContent from "./patient-drawer-content.component";
import { usePatientDrawer } from "./use-patient-drawer.hook";

export const DRAWER_WIDTH = 400;
export const DRAWER_MOBILE_WIDTH = 40;

export default function PatientDrawer() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("mobile"));
  const {
    isOpen,
    isFetching,
    patient,
    handleClose,
    disableClose,
    isCollapsed,
    setIsCollapsed,
    isCollapsible
  } = usePatientDrawer();
  const { setPatientId } = usePatientNotesDrawer();
  const drawerContentRef = useRef<HTMLDivElement | null>(null);
  const top = useGetTopBarHeight();

  useEffect(() => {
    setPatientId(patient?.id ?? -1);

    return () => {
      setPatientId(-1);
    };
  }, [patient?.id, setPatientId]);

  useEffect(() => {
    if (!isOpen && drawerContentRef.current) {
      drawerContentRef.current.scrollTop = 0;
      setIsCollapsed(false);
    }
  }, [isOpen, setIsCollapsed]);

  const handleCloseDrawer = useCallback(() => {
    if (isMobile && isCollapsible) {
      setIsCollapsed(true);
    } else {
      handleClose();
    }
  }, [handleClose, isCollapsible, isMobile, setIsCollapsed]);

  return (
    <Drawer
      PaperProps={{
        sx: {
          top,
          width: isMobile && isCollapsed ? DRAWER_MOBILE_WIDTH : DRAWER_WIDTH,
          background: isMobile && isCollapsed ? primary[400] : misc.mutedPurple,
          height: "calc(100% - 75px)",
          animationDelay: "0.5s"
        }
      }}
      variant={isMobile && !isCollapsed ? "temporary" : "persistent"}
      anchor="right"
      open={isOpen}
      onClose={handleCloseDrawer}
      className="animate__animated animate__fadeIn animate__faster"
      onClick={() => {
        if (isMobile && isCollapsed) {
          setIsCollapsed((prev) => !prev);
        }
      }}
      sx={{ zIndex: 2 }}
    >
      {isMobile && isCollapsible && (
        <Box
          sx={{
            position: "absolute",
            top: isCollapsed ? "50%" : 3,
            left: 1,
            width: "32px",
            height: "32px",
            border: `solid 1px ${grey[300]}`,
            boxShadow: theme.shadows[2],
            borderRadius: "50%",
            background: !isCollapsed ? primary[400] : "white"
          }}
        >
          <IconButton
            size="small"
            onClick={(event) => {
              event.stopPropagation();
              setIsCollapsed((prev) => !prev);
            }}
            onTouchStart={(event) => {
              event.stopPropagation();
              setIsCollapsed((prev) => !prev);
            }}
          >
            {isCollapsed ? (
              <ChevronLeftIcon sx={{ transform: "translate(-2px,-1px)" }} />
            ) : (
              <ChevronRightIcon
                sx={{ transform: "translate(0px,-1px)", color: "white" }}
              />
            )}
          </IconButton>
        </Box>
      )}
      {isMobile && isCollapsed ? null : (
        <PatientDrawerContent
          drawerContentRef={drawerContentRef}
          isFetching={isFetching}
          disableClose={disableClose}
          patient={patient}
          handleClose={handleClose}
        />
      )}
    </Drawer>
  );
}
